import { useContext, useEffect, useRef } from "react";
import ResizeObserver from "resize-observer-polyfill";
import { StickyContext } from "./StickyContext";
import { getHorizontalScrollParent, getStickerState } from "./stickerUtilities";
const EMPTY_REF = { current: undefined };
export const useStickyContext = ({ wrapperRef, ghostRef, positionerRef, positionParentRef: positionParentRefProp, sizerRef, isOverlay, isDisabled, isExcludedFromOffsets, zIndex }) => {
    const { stickersRef, debouncedUpdate, verticalScrollParentRef = EMPTY_REF } = useContext(StickyContext);
    const parentRef = useRef(null);
    const positionParentRef = positionParentRefProp || parentRef;
    const stickerRef = useRef(undefined);
    useEffect(() => {
        parentRef.current = wrapperRef.current?.parentElement ?? null;
        const stickers = stickersRef.current;
        const ghost = ghostRef.current;
        const positionParent = positionParentRef.current;
        const positioner = positionerRef.current;
        const sizer = sizerRef.current;
        const wrapper = wrapperRef.current;
        if (!stickers || !ghost || !positionParent || !positioner || !sizer || !wrapper) {
            return;
        }
        ghost.inert = true;
        const horizontalScrollParent = getHorizontalScrollParent(ghost);
        const verticalScrollParent = verticalScrollParentRef.current ?? undefined;
        // Register
        const sticker = {
            ghost,
            wrapper,
            positioner,
            sizer,
            positionParent,
            horizontalScrollParent,
            verticalScrollParent,
            isDisabled,
            isOverlay,
            zIndex,
            isExcludedFromOffsets: isExcludedFromOffsets ?? Boolean(isDisabled),
            // above are the props, below is mutable state, should it be separated into its own object?
            isSticky: false,
            isPinned: false,
            zIndexOffset: 0,
            stickyOffsetBottom: 0,
            stickyOffsetTop: 0,
            ...getStickerState({ ghost, positionParent, horizontalScrollParent, verticalScrollParent })
        };
        stickerRef.current = sticker;
        stickers.push(sticker);
        const onSyncWheel = (e) => {
            horizontalScrollParent.scrollLeft += e.deltaX;
        };
        positioner.addEventListener("wheel", onSyncWheel, { passive: true });
        return () => {
            positioner.removeEventListener("wheel", onSyncWheel);
            // unregister
            if (stickerRef.current) {
                stickers.splice(stickers.indexOf(stickerRef.current), 1);
                stickerRef.current = undefined;
            }
        };
    }, [
        ghostRef,
        isDisabled,
        isExcludedFromOffsets,
        isOverlay,
        positionParentRef,
        positionerRef,
        sizerRef,
        stickersRef,
        verticalScrollParentRef,
        wrapperRef,
        zIndex
    ]);
    useEffect(() => {
        debouncedUpdate();
        return () => {
            debouncedUpdate.cancel();
        };
    }, [isDisabled, debouncedUpdate]);
    useEffect(() => {
        const resizeObserver = new ResizeObserver(debouncedUpdate);
        if (ghostRef.current) {
            resizeObserver.observe(ghostRef.current);
        }
        if (positionParentRef.current) {
            resizeObserver.observe(positionParentRef.current);
        }
        return () => {
            resizeObserver.disconnect();
            debouncedUpdate.cancel();
        };
    }, [parentRef, ghostRef, debouncedUpdate, positionParentRef]);
    // Strip ids from ghost to prevent duplicate id a11y issues
    useEffect(() => {
        const ghost = ghostRef.current;
        if (!ghost) {
            return;
        }
        const stripIds = () => {
            ghost.querySelectorAll("[id]")?.forEach((element) => element.removeAttribute("id"));
        };
        stripIds();
        const mutationObserver = new MutationObserver(stripIds);
        mutationObserver.observe(ghost, { childList: true, subtree: true, attributes: true, attributeFilter: ["id"] });
        return () => {
            mutationObserver.disconnect();
        };
    }, [ghostRef]);
    return stickerRef.current;
};
